import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, ScrollText, Home as HomeIcon, User, HandHeart } from "lucide-react";
import { ShareButton, guideSharePayload } from "@/components/share/ShareButton";

/**
 * Static reference page — "Ripartizione Spese Proprietario e Inquilino".
 * Content is intentionally embedded (no CMS / DB entry) so nothing is
 * added or overwritten in the database.
 * Reading-optimised for smartphones: single-column, sticky legend,
 * high-contrast P/I chips, category-anchored ToC on desktop.
 */

const P = "P";
const I = "I";
const SPLIT = "SPLIT"; // 10% P / 90% I

const SECTIONS = [
    {
        id: "ascensore",
        title: "Ascensore",
        items: [
            ["Manutenzione ordinaria e piccole riparazioni", I],
            ["Installazione e manutenzione straordinaria degli impianti", P],
            ["Adeguamento alle nuove disposizioni di legge", P],
            ["Consumi energia elettrica per forza motrice e illuminazione", I],
            ["Ispezioni e collaudi", I],
        ],
    },
    {
        id: "autoclave",
        title: "Autoclave",
        items: [
            ["Installazione e sostituzione integrale dell'impianto o di componenti primari", P],
            ["Manutenzione ordinaria", I],
            ["Imposte e tasse di impianto", P],
            ["Forza motrice", I],
            ["Ricarico pressione del serbatoio", I],
            ["Ispezioni, collaudi e lettura contatori", I],
        ],
    },
    {
        id: "illuminazione-videocitofono",
        title: "Impianti di illuminazione, videocitofono, videosorveglianza e speciali",
        items: [
            ["Installazione e sostituzione dell'impianto comune di illuminazione", P],
            ["Manutenzione ordinaria dell'impianto comune di illuminazione", I],
            ["Installazione e sostituzione degli impianti di suoneria e allarme", P],
            ["Manutenzione ordinaria degli impianti di suoneria e allarme", I],
            ["Installazione e sostituzione dei citofoni e videocitofoni", P],
            ["Manutenzione ordinaria dei citofoni e videocitofoni", I],
            ["Installazione e sostituzione di impianti speciali di allarme, sicurezza e simili", P],
            ["Manutenzione ordinaria di impianti speciali di allarme, sicurezza e simili", I],
            ["Installazione e sostituzione di impianti di videosorveglianza", P],
            ["Manutenzione ordinaria di impianti di videosorveglianza", I],
        ],
    },
    {
        id: "riscaldamento",
        title: "Impianti di riscaldamento, condizionamento, acqua calda, addolcimento acqua, energie rinnovabili",
        items: [
            ["Installazione e sostituzione degli impianti", P],
            ["Adeguamento degli impianti a leggi e regolamenti", P],
            ["Manutenzione ordinaria degli impianti, compreso il rivestimento refrattario", I],
            ["Pulizia annuale degli impianti e dei filtri e messa a riposo stagionale", I],
            ["Lettura dei contatori", I],
            ["Acquisto combustibile, consumi di forza motrice, energia elettrica e acqua", I],
        ],
    },
    {
        id: "sportivi",
        title: "Impianti sportivi",
        items: [
            ["Installazione e manutenzione straordinaria", P],
            ["Addetti (bagnini, pulitori, manutentori ordinari ecc.)", I],
            ["Consumo di acqua per pulizia e depurazione; acquisto di materiale per la manutenzione ordinaria", I],
        ],
    },
    {
        id: "antincendio",
        title: "Impianto antincendio",
        items: [
            ["Installazione e sostituzione dell'impianto", P],
            ["Acquisto degli estintori", P],
            ["Manutenzione ordinaria", I],
            ["Ricarica degli estintori, ispezioni e collaudi", I],
        ],
    },
    {
        id: "radiotv",
        title: "Impianto centralizzato di ricezione radiotelevisiva e flussi informativi",
        items: [
            ["Installazione, sostituzione o potenziamento dell'impianto", P],
            ["Manutenzione ordinaria dell'impianto", I],
        ],
    },
    {
        id: "parti-comuni",
        title: "Parti comuni",
        items: [
            ["Sostituzione di grondaie, sifoni e colonne di scarico", P],
            ["Manutenzione ordinaria grondaie, sifoni e colonne di scarico", I],
            ["Manutenzione straordinaria di tetti e lastrici solari", P],
            ["Manutenzione ordinaria dei tetti e lastrici solari", I],
            ["Manutenzione straordinaria della rete di fognatura", P],
            ["Manutenzione ordinaria della rete di fognatura, compresa la disostruzione dei condotti e pozzetti", I],
            ["Sostituzione di marmi, corrimano, ringhiere", P],
            ["Manutenzione ordinaria di pareti, corrimano, ringhiere di scale e locali comuni", I],
            ["Consumo di acqua ed energia elettrica per le parti comuni", I],
            ["Installazione e sostituzione di serrature", P],
            ["Manutenzione delle aree verdi, compresa la riparazione degli attrezzi utilizzati", I],
            ["Installazione di attrezzature quali caselle postali, cartelli segnalatori, bidoni, armadietti per contatori, zerbini, tappeti, guide e altro materiale di arredo", P],
            ["Manutenzione ordinaria delle stesse attrezzature", I],
            ["Tassa occupazione suolo pubblico per passo carrabile", I],
            ["Tassa occupazione suolo pubblico per lavori condominiali", P],
        ],
    },
    {
        id: "parti-interne",
        title: "Parti interne all'appartamento locato",
        items: [
            ["Sostituzione integrale di pavimenti e rivestimenti", P],
            ["Manutenzione ordinaria di pavimenti e rivestimenti", I],
            ["Manutenzione ordinaria di infissi e serrande, dell'impianto sanitario", I],
            ["Rifacimento di chiavi e serrature", I],
            ["Tinteggiatura di pareti", I],
            ["Sostituzione di vetri", I],
            ["Manutenzione ordinaria di apparecchi e condutture di elettricità, cavi, impianti citofonico, videocitofonico e individuali", I],
            ["Verniciatura di opere in legno e metallo", I],
            ["Manutenzione ordinaria dell'impianto di riscaldamento e condizionamento", I],
            ["Manutenzione straordinaria dell'impianto di riscaldamento e condizionamento", P],
        ],
    },
    {
        id: "portierato",
        title: "Portierato",
        items: [
            ["Trattamento economico del portiere e del sostituto", SPLIT],
            ["Materiale per le pulizie", I],
            ["Indennità sostitutiva alloggio portiere", SPLIT],
            ["Manutenzione ordinaria della guardiola", SPLIT],
            ["Manutenzione straordinaria della guardiola", P],
        ],
    },
    {
        id: "pulizia",
        title: "Pulizia",
        items: [
            ["Spese per l'assunzione dell'addetto", P],
            ["Trattamento economico dell'addetto", I],
            ["Spese per il conferimento dell'appalto a ditta", P],
            ["Spese per le pulizie appaltate a ditta", I],
            ["Materiale per le pulizie", I],
            ["Acquisto e sostituzione macchinari per la pulizia", P],
            ["Manutenzione ordinaria dei macchinari per la pulizia", I],
            ["Derattizzazione e disinfestazione dei locali legati alla raccolta delle immondizie", I],
            ["Disinfestazione di bidoni e contenitori di rifiuti", I],
            ["Tassa rifiuti o tariffa sostitutiva", I],
            ["Acquisto di bidoni, trespoli e contenitori", P],
            ["Sacchi per la preraccolta dei rifiuti", I],
        ],
    },
    {
        id: "sgombero-neve",
        title: "Sgombero neve",
        items: [
            ["Spese relative al servizio, compresi i materiali d'uso", I],
        ],
    },
];

function Chip({ who }) {
    if (who === P) {
        return (
            <span
                data-testid="chip-P"
                title="Proprietario"
                className="inline-flex items-center justify-center shrink-0 size-8 rounded-full bg-primary/10 text-primary font-serif font-semibold text-sm border border-primary/25"
            >
                P
            </span>
        );
    }
    if (who === I) {
        return (
            <span
                data-testid="chip-I"
                title="Inquilino"
                className="inline-flex items-center justify-center shrink-0 size-8 rounded-full bg-amber-100 text-amber-900 font-serif font-semibold text-sm border border-amber-300"
            >
                I
            </span>
        );
    }
    // Split 10% P / 90% I
    return (
        <span
            data-testid="chip-split"
            title="Proprietario 10% / Inquilino 90%"
            className="inline-flex items-center gap-1 shrink-0 rounded-full px-2 py-1 bg-slate-100 border border-slate-300 text-[10px] font-semibold uppercase tracking-wide text-slate-700"
        >
            <span className="text-primary">P 10%</span>
            <span aria-hidden>·</span>
            <span className="text-amber-800">I 90%</span>
        </span>
    );
}

function ChipLegend({ who }) {
    // Larger legend variant used in the top banner
    if (who === P) {
        return (
            <span className="inline-flex items-center gap-2">
                <span className="inline-flex items-center justify-center size-7 rounded-full bg-primary/10 text-primary font-serif font-semibold text-sm border border-primary/25">P</span>
                <span className="text-sm text-slate-700"><strong className="text-slate-900">Proprietario</strong></span>
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center size-7 rounded-full bg-amber-100 text-amber-900 font-serif font-semibold text-sm border border-amber-300">I</span>
            <span className="text-sm text-slate-700"><strong className="text-slate-900">Inquilino</strong></span>
        </span>
    );
}

export default function RipartizioneSpese() {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return SECTIONS;
        return SECTIONS.map(s => ({
            ...s,
            items: s.items.filter(([label]) => label.toLowerCase().includes(q) || s.title.toLowerCase().includes(q)),
        })).filter(s => s.items.length > 0);
    }, [query]);

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 text-muted-foreground" data-testid="rip-back">
                <Link to="/"><ArrowLeft className="size-4 mr-1" /> Torna alla Home</Link>
            </Button>

            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ScrollText className="size-3.5" /> Guida rapida
            </div>
            <h1
                className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight"
                data-testid="ripartizione-title"
            >
                Ripartizione Spese<br className="sm:hidden" /> Proprietario e Inquilino
            </h1>
            <p className="mt-3 text-slate-600 leading-relaxed">
                Chi paga cosa nel condominio? Consulta la tabella per categoria. Le voci con una sola sigla sono a carico esclusivo di quel soggetto; le voci con ripartizione mostrano la quota indicativa.
            </p>

            {/* Discreet peer-help share strip — under the title */}
            <div
                data-testid="guide-share-block"
                className="mt-5 rounded-xl border border-primary/15 bg-primary/[0.04] px-4 sm:px-5 py-3 sm:py-3.5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            >
                <div className="flex items-start sm:items-center gap-2.5 flex-1 min-w-0">
                    <HandHeart className="size-4 text-accent shrink-0 mt-0.5 sm:mt-0" strokeWidth={2.4} />
                    <p className="text-[13.5px] sm:text-sm text-slate-700 leading-snug">
                        <span className="font-semibold text-slate-900">Aiuta chi ne ha bisogno.</span>{" "}
                        Conosci qualcuno con lo stesso dubbio? Condividi questa guida.
                    </p>
                </div>
                <ShareButton
                    payload={guideSharePayload({
                        path: "/faq/ripartizione-spese",
                        title: "Ripartizione spese Proprietario e Inquilino — CondominioNow",
                    })}
                    variant="compact"
                    label="Condividi"
                    data-testid="guide-share-ripartizione"
                    className="shrink-0"
                />
            </div>

            {/* Legend — sticky on mobile so è sempre visibile mentre si scorre */}
            <div
                data-testid="ripartizione-legend"
                className="sticky top-2 z-10 mt-6 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur px-4 py-3 shadow-sm"
            >
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Legenda</div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <ChipLegend who={P} />
                    <ChipLegend who={I} />
                    <span className="inline-flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 bg-slate-100 border border-slate-300 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                            <span className="text-primary">P 10%</span>·<span className="text-amber-800">I 90%</span>
                        </span>
                        <span className="text-sm text-slate-700">Ripartizione</span>
                    </span>
                </div>
            </div>

            {/* Filter */}
            <div className="mt-5 relative">
                <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Filtra per parola chiave (es. ascensore, pulizia, tetti)…"
                    className="pl-9 h-11"
                    data-testid="ripartizione-search"
                />
            </div>

            {/* Sections */}
            <div className="mt-8 space-y-8" data-testid="ripartizione-sections">
                {filtered.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 py-14 text-center text-muted-foreground">
                        Nessuna voce trovata per "{query}".
                    </div>
                ) : filtered.map(section => (
                    <section key={section.id} id={section.id} data-testid={`ripartizione-${section.id}`}>
                        <h2 className="font-serif text-xl sm:text-2xl font-semibold text-slate-900 mb-3 leading-tight">
                            {section.title}
                        </h2>
                        <ul className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
                            {section.items.map(([label, who], i) => (
                                <li
                                    key={i}
                                    className="flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4"
                                >
                                    <Chip who={who} />
                                    <span className="text-[15px] sm:text-base text-slate-800 leading-snug">{label}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                ))}
            </div>

            {/* Footer note */}
            <p className="mt-10 text-xs text-muted-foreground leading-relaxed border-t border-slate-200 pt-5">
                <strong>Nota</strong>: per le voci non previste si rinvia alle norme di legge e agli usi locali. Questa guida ha valore informativo e non sostituisce il parere di un professionista qualificato.
            </p>

            {/* CTA */}
            <div className="mt-8 rounded-2xl bg-slate-950 text-white p-6 sm:p-8 text-center">
                <h3 className="font-serif text-2xl font-semibold">Hai un dubbio sulla tua ripartizione?</h3>
                <p className="mt-2 text-slate-300 text-sm">Ogni situazione può avere regole particolari. Chiedi al nostro esperto.</p>
                <Button asChild size="lg" className="mt-5 bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full">
                    <Link to="/registrati">Fai una domanda</Link>
                </Button>
            </div>
        </div>
    );
}
